/**
 * アルコール判定の閾値（画面表示用）。
 *
 * 判定そのものはサーバーが行う。画面に「正常／要上長確認／出勤不可」を出すために
 * 同じ値が必要なので、GET /alert-settings/alcohol で受け取り localStorage に保持する。
 * 圏外でも直前の値で表示できるようにキャッシュしている。
 *
 * 閾値は会社ごと（さらに船・個人ごと）に変えられるため、画面側に数値を直接書かないこと。
 * 以前は 0.015 / 0.150 が各画面にハードコードされており、管理者が設定を変えても
 * 表示だけ元の値のままだった。
 */
const KEY = "catms_alcohol_thresholds";

export interface AlcoholThresholds {
  /** これ以上で「要上長確認」 */
  warning_mgl: number;
  /** これ以上で「出勤不可（法定基準値超過）」 */
  critical_mgl: number;
}

/** サーバーから取れないときの既定値（アラート設定の初期値と同じ） */
export const DEFAULT_ALCOHOL_THRESHOLDS: AlcoholThresholds = {
  warning_mgl:  0.015,
  critical_mgl: 0.150,
};

export function saveAlcoholThresholds(t: unknown): void {
  if (!t || typeof t !== "object") return;
  const { warning_mgl, critical_mgl } = t as Partial<AlcoholThresholds>;
  if (typeof warning_mgl !== "number" || typeof critical_mgl !== "number") return;
  try { localStorage.setItem(KEY, JSON.stringify({ warning_mgl, critical_mgl })); } catch { /* noop */ }
}

export function getAlcoholThresholds(): AlcoholThresholds {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_ALCOHOL_THRESHOLDS;
    const t = JSON.parse(raw) as Partial<AlcoholThresholds>;
    if (typeof t.warning_mgl !== "number" || typeof t.critical_mgl !== "number") return DEFAULT_ALCOHOL_THRESHOLDS;
    return { warning_mgl: t.warning_mgl, critical_mgl: t.critical_mgl };
  } catch { return DEFAULT_ALCOHOL_THRESHOLDS; }
}

export function clearAlcoholThresholds(): void {
  try { localStorage.removeItem(KEY); } catch { /* noop */ }
}

export type AlcoholJudgement = "normal" | "warning" | "critical";

/** 測定値の判定。未測定(null)は「正常」扱い（測定必須かどうかは別の仕組みで見る） */
export function judgeAlcohol(
  mgL: number | null | undefined,
  t: AlcoholThresholds = getAlcoholThresholds(),
): AlcoholJudgement {
  if (mgL == null) return "normal";
  if (mgL >= t.critical_mgl) return "critical";
  if (mgL >= t.warning_mgl)  return "warning";
  return "normal";
}

/** 「0.015 mg/L」のような表示用文字列 */
export function fmtMgL(v: number): string {
  return `${v.toFixed(3)} mg/L`;
}
